/* global Excel console */
/**
 * Excel Renderer for JESS (JSON Excel Style Schema) Reports.
 *
 * v6.0.1: Fixes merge/values dimension mismatch and null safety.
 * Renders structured LLM output directly into styled Excel sheets.
 * Maps JESS types to Excel.js API calls for rich formatting.
 */

import {
  ExcelStyledReport,
  Section,
  MetricCard,
  ListItem,
  Paragraph,
  DataTable,
  Badge,
  BadgeType,
  SectionStylePreset,
  getSectionIcon,
  getBadgeColors,
  getSectionColors,
  getRankFromColors,
  RANK_BADGE_COLORS,
  stripMarkdown,
  calculateRowHeight,
} from "./excelStyleTypes";
import { THEME, ASIN_HEADER_THEME } from "./constants";
import { formatDateRangeLabel } from "./excelHelpers";

// Total columns used for layout
const TOTAL_COLS = 12;

// ============================================================
// CORE STYLING UTILITIES
// ============================================================

/**
 * Merge a range and set a value on its first cell.
 *
 * Excel.js requires the values array to match the original range dimensions,
 * even after merging. Setting the value on just the first cell (1x1) avoids
 * the "number of rows or columns doesn't match" error.
 *
 * Returns the full merged range so formatting can be applied to it.
 */
function mergeAndSet(
  sheet: Excel.Worksheet,
  row: number,
  col: number,
  rowSpan: number,
  colSpan: number,
  value: any
): Excel.Range {
  // Set value on the first cell only (1x1 range)
  sheet.getRangeByIndexes(row, col, 1, 1).values = [[value]];
  // Get the full range and merge it
  const range = sheet.getRangeByIndexes(row, col, rowSpan, colSpan);
  if (rowSpan > 1 || colSpan > 1) {
    range.merge();
  }
  return range;
}

/**
 * Apply font styling to a range.
 */
function applyFont(
  range: Excel.Range,
  opts: {
    size?: number;
    bold?: boolean;
    italic?: boolean;
    color?: string;
    name?: string;
  }
): void {
  if (opts.size !== undefined) range.format.font.size = opts.size;
  if (opts.bold !== undefined) range.format.font.bold = opts.bold;
  if (opts.italic !== undefined) range.format.font.italic = opts.italic;
  if (opts.color !== undefined) range.format.font.color = opts.color;
  if (opts.name !== undefined) range.format.font.name = opts.name;
}

/**
 * Apply all four borders to a range.
 */
function applyBorders(
  range: Excel.Range,
  color: string,
  style: Excel.BorderLineStyle = Excel.BorderLineStyle.continuous
): void {
  const edges: Excel.BorderIndex[] = [
    Excel.BorderIndex.edgeTop,
    Excel.BorderIndex.edgeBottom,
    Excel.BorderIndex.edgeLeft,
    Excel.BorderIndex.edgeRight,
  ];
  for (const edge of edges) {
    const border = range.format.borders.getItem(edge);
    border.style = style;
    border.color = color;
  }
}

// ============================================================
// SECTION RENDERERS
// ============================================================

/**
 * Render the dashboard header with title and executive summary.
 */
function renderDashboardHeader(
  sheet: Excel.Worksheet,
  report: ExcelStyledReport,
  appliedFilters: string,
  startRow: number,
  title: string
): number {
  let row = startRow;

  // Title bar
  const titleRange = mergeAndSet(sheet, row, 0, 1, TOTAL_COLS, title);
  applyFont(titleRange, { size: 16, bold: true, color: THEME.headerText });
  titleRange.format.fill.color = THEME.headerBg;
  titleRange.format.rowHeight = 40;
  titleRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
  titleRange.format.verticalAlignment = Excel.VerticalAlignment.center;
  row++;

  // Applied filters (if any)
  if (appliedFilters) {
    const filterRange = mergeAndSet(sheet, row, 0, 1, TOTAL_COLS, appliedFilters);
    applyFont(filterRange, { size: 10, italic: true, color: "#6B7280" });
    filterRange.format.fill.color = THEME.primaryLight;
    filterRange.format.wrapText = true;
    filterRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
    filterRange.format.verticalAlignment = Excel.VerticalAlignment.top;
    filterRange.format.rowHeight = calculateRowHeight(appliedFilters, 700);
    row++;
  }

  // Data period label
  const periodLabel = formatDateRangeLabel(report.start_date, report.end_date);
  const periodRange = mergeAndSet(sheet, row, 0, 1, TOTAL_COLS, periodLabel);
  applyFont(periodRange, { size: 10, italic: true, color: "#6B7280" });
  periodRange.format.fill.color = THEME.primaryLight;
  periodRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
  periodRange.format.rowHeight = 22;
  row++;

  // Executive summary
  const summaryText = stripMarkdown(report.executive_summary);
  const summaryRange = mergeAndSet(sheet, row, 0, 1, TOTAL_COLS, summaryText);
  applyFont(summaryRange, { size: 11, color: THEME.primaryDark });
  summaryRange.format.fill.color = THEME.primaryLight;
  summaryRange.format.wrapText = true;
  const summaryHeight = calculateRowHeight(summaryText, 700);
  summaryRange.format.rowHeight = Math.max(summaryHeight, 50);
  summaryRange.format.horizontalAlignment = Excel.HorizontalAlignment.left;
  summaryRange.format.verticalAlignment = Excel.VerticalAlignment.center;
  applyBorders(summaryRange, THEME.cardBorder);
  row++;

  // Spacer
  sheet.getRangeByIndexes(row, 0, 1, 1).format.rowHeight = 8;
  row++;

  return row;
}

/**
 * Render top-level KPI metrics in a dashboard grid (3 per row).
 */
function renderTopMetrics(sheet: Excel.Worksheet, metrics: MetricCard[], startRow: number): number {
  if (!metrics || metrics.length === 0) return startRow;

  let row = startRow;

  // Section header
  const headerRange = mergeAndSet(sheet, row, 0, 1, TOTAL_COLS, "Key Metrics");
  applyFont(headerRange, { size: 12, bold: true, color: THEME.sectionText });
  headerRange.format.fill.color = THEME.sectionBg;
  headerRange.format.rowHeight = 28;
  headerRange.format.horizontalAlignment = Excel.HorizontalAlignment.left;
  headerRange.format.verticalAlignment = Excel.VerticalAlignment.center;
  row++;

  const metricsPerRow = 3;
  const colsPerMetric = 4; // Each metric spans 4 columns

  for (let i = 0; i < metrics.length; i++) {
    const metric = metrics[i];
    const colOffset = (i % metricsPerRow) * colsPerMetric;
    const metricRow = row + Math.floor(i / metricsPerRow) * 3;

    // Card background
    const cardRange = sheet.getRangeByIndexes(metricRow, colOffset, 2, colsPerMetric);
    cardRange.format.fill.color = THEME.cardBg;
    applyBorders(cardRange, THEME.cardBorder);

    // Label row
    const labelRange = mergeAndSet(sheet, metricRow, colOffset, 1, colsPerMetric, metric.label);
    applyFont(labelRange, { size: 10, color: THEME.cardText });
    labelRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
    labelRange.format.verticalAlignment = Excel.VerticalAlignment.center;
    labelRange.format.rowHeight = 20;

    // Value row (leave last col for badge)
    const valueColSpan = metric.badge ? colsPerMetric - 1 : colsPerMetric;
    // Set number format to text BEFORE writing the value so Excel doesn't
    // auto-convert currency symbols (e.g. "$" → "₹") based on system locale.
    sheet.getRangeByIndexes(metricRow + 1, colOffset, 1, 1).numberFormat = [["@"]];
    const valueRange = mergeAndSet(sheet, metricRow + 1, colOffset, 1, valueColSpan, metric.value);
    applyFont(valueRange, { size: 16, bold: true, color: THEME.primaryDark });
    valueRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
    valueRange.format.verticalAlignment = Excel.VerticalAlignment.center;
    valueRange.format.rowHeight = 36;

    // Badge
    if (metric.badge) {
      const badgeColors = getBadgeColors(metric.badge.type);
      const badgeRange = sheet.getRangeByIndexes(
        metricRow + 1,
        colOffset + colsPerMetric - 1,
        1,
        1
      );
      badgeRange.values = [[metric.badge.text]];
      applyFont(badgeRange, { size: 9, bold: true, color: badgeColors.text });
      badgeRange.format.fill.color = badgeColors.bg;
      badgeRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
      badgeRange.format.verticalAlignment = Excel.VerticalAlignment.center;
      applyBorders(badgeRange, badgeColors.border);
    }
  }

  const totalMetricRows = Math.ceil(metrics.length / metricsPerRow) * 3;
  return row + totalMetricRows + 1; // +1 spacer
}

/**
 * Match the start of a keyword block emitted by the ADVERTISE_MORE prompt
 * (see services/market_watch_service.py, CATEGORY EVIDENCE section).
 *
 * Tolerant of LLM drift: leading whitespace, an optional bullet prefix
 * (`- `, `• `, `* `), and smart/curly quotes in addition to straight
 * single/double quotes. Matches only the START of a keyword line; the
 * block boundary is determined by the next match or end-of-text.
 */
const KEYWORD_BLOCK_REGEX = /^[ \t]*[-•*]?[ \t]*Keyword:\s*['"\u2018\u2019\u201C\u201D]/gm;

/**
 * Post-process JESS list items so that an `Action Steps` item containing
 * multiple `Keyword: '...'` paragraphs becomes (a) one root item with the
 * intro text (before the first keyword line) and (b) one indented sub-item
 * per keyword block. Items without ≥2 keyword blocks pass through unchanged.
 *
 * Exported for unit testing.
 */
export function splitKeywordBlocks(items: ListItem[]): ListItem[] {
  const out: ListItem[] = [];
  for (const item of items) {
    if (!item?.text) {
      out.push(item);
      continue;
    }

    // Find every keyword-line start offset. Normalize CRLF so `^`/`$`
    // anchors behave consistently regardless of LLM line-ending output.
    const text = item.text.replace(/\r\n/g, "\n");
    KEYWORD_BLOCK_REGEX.lastIndex = 0;
    const starts: number[] = [];
    let m: RegExpExecArray | null;
    while ((m = KEYWORD_BLOCK_REGEX.exec(text)) !== null) {
      starts.push(m.index);
    }

    if (starts.length < 2) {
      out.push(item);
      continue;
    }

    const parentIndent = item.indent_level ?? 0;

    // Intro = everything before the first keyword line.
    const intro = text.slice(0, starts[0]).trim();
    if (intro.length > 0) {
      out.push({
        ...item,
        text: intro,
        indent_level: parentIndent,
      });
    }

    // One sub-item per keyword block; badge is NOT propagated to sub-items.
    for (let i = 0; i < starts.length; i++) {
      const blockStart = starts[i];
      const blockEnd = i + 1 < starts.length ? starts[i + 1] : text.length;
      const blockText = text.slice(blockStart, blockEnd).trim();
      if (blockText.length === 0) continue;
      out.push({
        text: blockText,
        indent_level: parentIndent + 1,
      });
    }
  }
  return out;
}

/**
 * Render a single content section.
 */
function renderSection(sheet: Excel.Worksheet, section: Section, startRow: number): number {
  let row = startRow;
  const colors = getSectionColors((section.style_preset as SectionStylePreset) || "default");

  // Section header
  const icon = getSectionIcon(section.icon);
  const headerText = icon ? `${icon}  ${section.title}` : section.title;
  const headerRange = mergeAndSet(sheet, row, 0, 1, TOTAL_COLS, `  ${headerText}`);
  applyFont(headerRange, { size: 13, bold: false, color: colors.headerText });
  headerRange.format.fill.color = colors.header;
  headerRange.format.rowHeight = 36;
  headerRange.format.horizontalAlignment = Excel.HorizontalAlignment.left;
  headerRange.format.verticalAlignment = Excel.VerticalAlignment.center;
  row++;

  // Summary callout
  if (section.summary) {
    let text = stripMarkdown(section.summary);
    if (section.tag_insight) {
      text += `\n\n${stripMarkdown(section.tag_insight)}`;
    }
    const summaryRange = mergeAndSet(sheet, row, 0, 1, TOTAL_COLS, `  ${text}`);
    applyFont(summaryRange, { size: 11, italic: true, color: colors.accent });
    summaryRange.format.fill.color = colors.body;
    summaryRange.format.wrapText = true;
    const sectionSummaryHeight = calculateRowHeight(text, 700);
    summaryRange.format.rowHeight = Math.max(sectionSummaryHeight, 36);
    summaryRange.format.horizontalAlignment = Excel.HorizontalAlignment.left;
    summaryRange.format.verticalAlignment = Excel.VerticalAlignment.center;
    applyBorders(summaryRange, colors.border);
    row++;
  }

  // Seasonality note (styled info bar above metrics, only for seasonal ASINs)
  if (section.seasonality_note) {
    row = renderSeasonalityNote(sheet, section.seasonality_note, row, colors);
  }

  // Metrics grid (2 per row)
  if (section.metrics && section.metrics.length > 0) {
    row = renderSectionMetrics(sheet, section.metrics, row, colors);
  }

  // List items — expand any multi-keyword Action Steps item into one
  // indented sub-item per keyword so each keyword renders in its own cell.
  if (section.list_items && section.list_items.length > 0) {
    const expandedItems = splitKeywordBlocks(section.list_items);
    row = renderListItems(sheet, expandedItems, row, colors);
  }

  // Paragraphs
  if (section.paragraphs && section.paragraphs.length > 0) {
    row = renderParagraphs(sheet, section.paragraphs, row, colors);
  }

  // Data table
  if (section.table) {
    row = renderDataTable(sheet, section.table, row, colors);
  }

  // Spacer after section
  sheet.getRangeByIndexes(row, 0, 1, 1).format.rowHeight = 14;
  row++;

  return row;
}

/**
 * Render metrics within a section (2 per row).
 */
function renderSectionMetrics(
  sheet: Excel.Worksheet,
  metrics: MetricCard[],
  startRow: number,
  colors: { header: string; headerText: string; body: string; accent: string; border: string }
): number {
  let row = startRow;
  const metricsPerRow = 2;
  const colsPerMetric = 6;

  for (let i = 0; i < metrics.length; i++) {
    const metric = metrics[i];
    const colOffset = (i % metricsPerRow) * colsPerMetric;
    const metricRow = row + Math.floor(i / metricsPerRow) * 2;

    // Card background
    const cardRange = sheet.getRangeByIndexes(metricRow, colOffset, 2, colsPerMetric);
    cardRange.format.fill.color = colors.body;
    applyBorders(cardRange, colors.border);

    // Label
    const labelSpan = metric.badge ? colsPerMetric - 1 : colsPerMetric;
    const labelRange = mergeAndSet(sheet, metricRow, colOffset, 1, labelSpan, metric.label);
    applyFont(labelRange, { size: 10, color: colors.accent });
    labelRange.format.horizontalAlignment = Excel.HorizontalAlignment.left;
    labelRange.format.verticalAlignment = Excel.VerticalAlignment.center;
    labelRange.format.rowHeight = 20;

    // Value — force text format so Excel doesn't convert "$" to local currency
    sheet.getRangeByIndexes(metricRow + 1, colOffset, 1, 1).numberFormat = [["@"]];
    const valueRange = mergeAndSet(sheet, metricRow + 1, colOffset, 1, labelSpan, metric.value);
    applyFont(valueRange, { size: 14, bold: true, color: colors.header });
    valueRange.format.horizontalAlignment = Excel.HorizontalAlignment.left;
    valueRange.format.verticalAlignment = Excel.VerticalAlignment.center;
    valueRange.format.rowHeight = 28;

    // Badge — use rank-harmonized colors when inside a rank section
    if (metric.badge) {
      const rank = getRankFromColors(colors);
      const badgeColors = rank ? RANK_BADGE_COLORS[rank] : getBadgeColors(metric.badge.type);
      const badgeRange = mergeAndSet(
        sheet,
        metricRow,
        colOffset + colsPerMetric - 1,
        2,
        1,
        metric.badge.text
      );
      applyFont(badgeRange, { size: 8, bold: true, color: badgeColors.text });
      badgeRange.format.fill.color = badgeColors.bg;
      badgeRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
      badgeRange.format.verticalAlignment = Excel.VerticalAlignment.center;
      badgeRange.format.wrapText = true;
      applyBorders(badgeRange, badgeColors.border);
    }
  }

  const totalRows = Math.ceil(metrics.length / metricsPerRow) * 2;
  return row + totalRows;
}

/**
 * Render list items with bullets and badges.
 */
function renderListItems(
  sheet: Excel.Worksheet,
  items: ListItem[],
  startRow: number,
  colors: { header: string; headerText: string; body: string; accent: string; border: string }
): number {
  let row = startRow;

  for (const item of items) {
    if (!item.text) continue; // Skip items with missing text

    const indent = item.indent_level || 0;
    const bullet = indent > 0 ? "\u25E6" : "\u2022"; // ◦ for sub-items, • for main
    // Indent via leading whitespace (not column offset) so the row's fill
    // color extends to the left edge instead of leaving an unstyled gap.
    const indentSpaces = "    ".repeat(indent);
    const isKeywordBlock = /^Keyword:\s*['"‘’“”]/.test(item.text || "");
    const text = isKeywordBlock
      ? stripMarkdown(item.text)
      : `${indentSpaces}${bullet}  ${stripMarkdown(item.text)}`;
    const textSpan = item.badge ? 10 : TOTAL_COLS;

    // Text cell — always starts at column 0 so the body color fills the row.
    const textRange = mergeAndSet(sheet, row, 0, 1, textSpan, text);
    applyFont(textRange, {
      size: 11,
      color: colors.accent,
      bold: false,
    });
    textRange.format.fill.color = colors.body;
    textRange.format.wrapText = true;
    // Keyword-block sub-items can be ~15+ wrapped lines; allow up to 600px
    // so the full keyword paragraph renders without being clipped.
    textRange.format.rowHeight = calculateRowHeight(text, 600, 600);
    textRange.format.horizontalAlignment = Excel.HorizontalAlignment.left;
    textRange.format.verticalAlignment = Excel.VerticalAlignment.top;

    // Light bottom border for visual separation between items
    const fullRowRange = sheet.getRangeByIndexes(row, 0, 1, TOTAL_COLS);
    const bottomBorder = fullRowRange.format.borders.getItem(Excel.BorderIndex.edgeBottom);
    bottomBorder.style = Excel.BorderLineStyle.continuous;
    bottomBorder.color = colors.border;

    // Badge — use rank-harmonized colors when inside a rank section
    if (item.badge) {
      const rank = getRankFromColors(colors);
      const badgeColors = rank ? RANK_BADGE_COLORS[rank] : getBadgeColors(item.badge.type);
      const badgeRange = mergeAndSet(sheet, row, 10, 1, 2, item.badge.text);
      applyFont(badgeRange, { size: 9, bold: true, color: badgeColors.text });
      badgeRange.format.fill.color = badgeColors.bg;
      badgeRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
      badgeRange.format.verticalAlignment = Excel.VerticalAlignment.center;
      applyBorders(badgeRange, badgeColors.border);
    }

    row++;
  }

  return row;
}

/**
 * Render paragraphs with style variants.
 */
function renderParagraphs(
  sheet: Excel.Worksheet,
  paragraphs: Paragraph[],
  startRow: number,
  colors: { header: string; headerText: string; body: string; accent: string; border: string }
): number {
  let row = startRow;

  for (const para of paragraphs) {
    if (!para.text) continue; // Skip paragraphs with missing text

    const text = stripMarkdown(para.text);
    const range = mergeAndSet(sheet, row, 0, 1, TOTAL_COLS, text);
    range.format.wrapText = true;
    range.format.rowHeight = calculateRowHeight(text, 700);

    // Style based on paragraph type
    switch (para.style) {
      case "callout":
        range.format.fill.color = "#FEF3C7";
        applyFont(range, { size: 11, italic: true, color: "#92400E" });
        applyBorders(range, "#FDE68A", Excel.BorderLineStyle.continuous);
        break;
      case "quote":
        range.format.fill.color = "#F3F4F6";
        applyFont(range, { size: 11, italic: true, color: "#6B7280" });
        applyBorders(range, "#E5E7EB");
        break;
      case "highlight":
        range.format.fill.color = colors.body;
        applyFont(range, { size: 11, bold: false, color: colors.header });
        applyBorders(range, colors.border);
        break;
      default:
        range.format.fill.color = "#FFFFFF";
        applyFont(range, { size: 11, color: colors.accent });
        break;
    }

    // Alignment
    range.format.horizontalAlignment =
      para.alignment === "center"
        ? Excel.HorizontalAlignment.center
        : para.alignment === "right"
          ? Excel.HorizontalAlignment.right
          : Excel.HorizontalAlignment.left;
    range.format.verticalAlignment = Excel.VerticalAlignment.top;

    row++;
  }

  return row;
}

/**
 * Render a data table with headers and styled rows.
 */
function renderDataTable(
  sheet: Excel.Worksheet,
  table: DataTable,
  startRow: number,
  colors: { header: string; headerText: string; body: string; accent: string; border: string }
): number {
  let row = startRow;
  const colCount = table.headers.length;

  // Calculate column span: distribute TOTAL_COLS evenly across table columns
  const colSpan = Math.max(1, Math.floor(TOTAL_COLS / colCount));

  // Header row
  for (let c = 0; c < colCount; c++) {
    const headerCell = mergeAndSet(sheet, row, c * colSpan, 1, colSpan, table.headers[c]);
    applyFont(headerCell, { size: 10, bold: false, color: "#FFFFFF" });
    headerCell.format.fill.color = colors.header;
    headerCell.format.horizontalAlignment = Excel.HorizontalAlignment.center;
    headerCell.format.verticalAlignment = Excel.VerticalAlignment.center;
    headerCell.format.rowHeight = 24;
    applyBorders(headerCell, colors.header);
  }
  row++;

  // Data rows
  for (let r = 0; r < table.rows.length; r++) {
    const dataRow = table.rows[r];
    const isHighlighted = table.highlight_first_row && r === 0;
    const isStriped = table.style_preset === "striped" && r % 2 === 1;

    for (let c = 0; c < dataRow.length && c < colCount; c++) {
      // Force text format BEFORE writing so Excel doesn't auto-convert
      // currency symbols (e.g. "$" → "₹") based on system locale.
      sheet.getRangeByIndexes(row, c * colSpan, 1, 1).numberFormat = [["@"]];
      const cell = mergeAndSet(sheet, row, c * colSpan, 1, colSpan, dataRow[c]);
      cell.format.horizontalAlignment = Excel.HorizontalAlignment.center;
      cell.format.verticalAlignment = Excel.VerticalAlignment.center;
      cell.format.rowHeight = 22;

      // Determine styling
      let bgColor = "#FFFFFF";
      let textColor = colors.accent;
      let isBold = false;

      if (isHighlighted) {
        bgColor = colors.body;
        textColor = colors.header;
        isBold = false;
      } else if (isStriped) {
        bgColor = "#F9FAFB";
      }

      if (table.highlight_column === c) {
        textColor = colors.header;
        isBold = false;
      }

      cell.format.fill.color = bgColor;
      applyFont(cell, { size: 10, color: textColor, bold: isBold });
      applyBorders(cell, colors.border, Excel.BorderLineStyle.continuous);
    }
    row++;
  }

  // Spacer after table
  row++;
  return row;
}

/**
 * Render footer with timestamp and metadata.
 */
function renderFooter(sheet: Excel.Worksheet, report: ExcelStyledReport, startRow: number): number {
  const parts: string[] = [];
  if (report.generated_at) parts.push(`Generated: ${report.generated_at}`);
  if (report.data_source) parts.push(`Source: ${report.data_source}`);
  parts.push(`Generated: ${new Date().toLocaleString()}`);

  const footerRange = mergeAndSet(sheet, startRow, 0, 1, TOTAL_COLS, parts.join("  |  "));
  applyFont(footerRange, { size: 9, italic: true, color: "#9CA3AF" });
  footerRange.format.horizontalAlignment = Excel.HorizontalAlignment.right;
  footerRange.format.rowHeight = 20;

  return startRow + 1;
}

// ============================================================
// ASIN GROUPING HELPERS
// ============================================================

interface AsinGroup {
  asin: string;
  sections: { section: Section; isPrimary: boolean }[];
}

/**
 * Extract ASIN from a section title and group sections by ASIN.
 * Returns null if no section titles contain ASIN patterns (e.g. RCA reports).
 */
function groupSectionsByAsin(sections: Section[]): AsinGroup[] | null {
  const asinRegex = /\(([A-Z0-9]{5,})\)/;
  const groupMap = new Map<string, Section[]>();
  let matchCount = 0;

  for (const section of sections) {
    const match = section.title.match(asinRegex);
    if (match) {
      matchCount++;
      const asin = match[1];
      if (!groupMap.has(asin)) {
        groupMap.set(asin, []);
      }
      groupMap.get(asin)!.push(section);
    }
  }

  // If fewer than half the sections have ASINs, treat as non-Market-Watch
  if (matchCount === 0 || matchCount < sections.length / 2) return null;

  const groups: AsinGroup[] = [];
  groupMap.forEach((secs, asin) => {
    groups.push({
      asin,
      sections: secs.map((s, i) => ({ section: s, isPrimary: i === 0 })),
    });
  });
  return groups;
}

/**
 * Render a subtle grey ASIN banner header.
 */
function renderAsinHeader(sheet: Excel.Worksheet, asin: string, startRow: number): number {
  const range = mergeAndSet(sheet, startRow, 0, 1, TOTAL_COLS, `  ASIN: ${asin}`);
  applyFont(range, { size: 13, bold: false, color: ASIN_HEADER_THEME.text });
  range.format.fill.color = ASIN_HEADER_THEME.bg;
  range.format.rowHeight = 36;
  range.format.horizontalAlignment = Excel.HorizontalAlignment.left;
  range.format.verticalAlignment = Excel.VerticalAlignment.center;
  applyBorders(range, ASIN_HEADER_THEME.border);
  return startRow + 1;
}

/**
 * Render a seasonality summary callout row for an ASIN.
 * Uses the info color preset for a light blue callout style.
 */
function renderSeasonalitySummary(sheet: Excel.Worksheet, summary: string, startRow: number): number {
  const text = `  \uD83D\uDCC5 Seasonality: ${stripMarkdown(summary)}`;
  const range = mergeAndSet(sheet, startRow, 0, 1, TOTAL_COLS, text);
  applyFont(range, { size: 11, italic: true, color: "#0369A1" });
  range.format.fill.color = "#F0F9FF";
  range.format.wrapText = true;
  range.format.rowHeight = calculateRowHeight(text, 700);
  range.format.horizontalAlignment = Excel.HorizontalAlignment.left;
  range.format.verticalAlignment = Excel.VerticalAlignment.top;
  applyBorders(range, "#BAE6FD");
  return startRow + 1;
}

/**
 * Render a per-section seasonality note as plain text above metric cards.
 * Uses the section's body/accent colors to blend naturally into the section.
 */
function renderSeasonalityNote(
  sheet: Excel.Worksheet,
  note: string,
  startRow: number,
  colors: { header: string; body: string; accent: string; border: string }
): number {
  const text = `  ${stripMarkdown(note)}`;
  const range = mergeAndSet(sheet, startRow, 0, 1, TOTAL_COLS, text);
  applyFont(range, { size: 10, italic: true, color: colors.accent });
  range.format.fill.color = colors.body;
  range.format.wrapText = true;
  range.format.rowHeight = 30;
  range.format.horizontalAlignment = Excel.HorizontalAlignment.left;
  range.format.verticalAlignment = Excel.VerticalAlignment.center;
  applyBorders(range, colors.border);
  return startRow + 1;
}

/**
 * Build a combined section title: "Primary \u2014 Rule Name" or "Secondary \u2014 Rule Name".
 * Strips the ASIN and recommendation prefix, then prepends the label.
 * Also handles legacy "Recommendation N (ASIN): " format.
 */
function buildSectionTitle(title: string, isPrimary: boolean): string {
  const ruleName = title
    .replace(/^(Primary|Secondary)\s+Recommendation\s*\([A-Z0-9]+\):\s*/i, "")
    .replace(/^Recommendation\s+\d+\s*\([A-Z0-9]+\):\s*/i, "")
    .trim();
  const label = isPrimary ? "Primary Recommendation" : "Secondary Recommendation";
  return `${label} \u2014 ${ruleName}`;
}

// ============================================================
// ERROR REPORT DETECTION & SANITIZATION
// ============================================================

/**
 * Detect whether a JESS report represents a backend error.
 */
export function isErrorReport(report: ExcelStyledReport): boolean {
  if (!report) return false;
  if (report.executive_summary?.startsWith("Analysis encountered an error:")) {
    return true;
  }
  if (
    report.sections?.length > 0 &&
    report.sections[0].title === "Error During Analysis"
  ) {
    return true;
  }
  return false;
}

/**
 * Extract the human-readable error message from a raw backend error string.
 *
 * Handles two patterns:
 * 1. Google API errors with nested dict containing a 'message' field
 *    (Python str() uses single quotes: 'message': 'API key not valid...')
 * 2. Plain exception strings (returned as-is after stripping status prefix)
 */
function extractErrorMessage(rawError: string): string {
  const stripped = rawError.startsWith("Analysis encountered an error:")
    ? rawError.slice("Analysis encountered an error:".length).trim()
    : rawError;

  // Python dict format: 'message': 'some error text'
  const singleQuoteMatch = stripped.match(/'message'\s*:\s*'([^']*)'/);
  if (singleQuoteMatch) {
    return singleQuoteMatch[1];
  }

  // JSON format fallback: "message": "some error text"
  const doubleQuoteMatch = stripped.match(/"message"\s*:\s*"([^"]*)"/);
  if (doubleQuoteMatch) {
    return doubleQuoteMatch[1];
  }

  // Plain exception string: strip leading status code if present
  // e.g. "400 INVALID_ARGUMENT. Some message" → "Some message"
  const statusStripped = stripped.replace(/^\d{3}\s+[A-Z_]+\.\s*/, "");
  return statusStripped || stripped;
}

/**
 * Replace raw backend error details with the extracted user-friendly message.
 * Returns a new report object (does not mutate the original).
 * For non-error reports, returns the input unchanged (no-op).
 */
export function sanitizeErrorReport(report: ExcelStyledReport): ExcelStyledReport {
  if (!isErrorReport(report)) return report;

  // Extract from executive_summary first, fall back to first list item text
  let friendlyMessage = extractErrorMessage(report.executive_summary || "");
  if (!friendlyMessage && report.sections?.[0]?.list_items?.[0]?.text) {
    const itemText = report.sections[0].list_items[0].text.replace(/^\*\*Error:\*\*\s*/, "");
    friendlyMessage = extractErrorMessage(itemText);
  }
  if (!friendlyMessage) {
    friendlyMessage = "An unexpected error occurred. Please try again.";
  }

  return {
    ...report,
    executive_summary: friendlyMessage,
    sections: [
      {
        title: "Analysis Unavailable",
        icon: "warning",
        style_preset: "warning",
        summary:
          "Market Watch was unable to generate recommendations for this run. Please try again.",
        list_items: [
          {
            text: friendlyMessage,
            badge: { text: "Error", type: "critical" as const },
          },
        ],
      },
    ],
  };
}

// ============================================================
// MAIN RENDER FUNCTION
// ============================================================

/**
 * Render an ExcelStyledReport to a new worksheet.
 *
 * @param context - Excel request context
 * @param report - The structured JESS report from LLM
 * @param sheetName - Optional sheet name (auto-generated if not provided)
 * @param appliedFilters - Optional applied filters summary string
 * @returns The created worksheet
 */
export async function renderExcelStyledReport(
  context: Excel.RequestContext,
  report: ExcelStyledReport,
  sheetName?: string,
  appliedFilters?: string,
  title?: string
): Promise<Excel.Worksheet> {
  // Generate sheet name with timestamp
  const timestamp = new Date().toLocaleTimeString().replace(/:/g, "-");
  const finalSheetName = sheetName || `Analysis ${timestamp}`;

  // Create or clear sheet
  let sheet: Excel.Worksheet;
  const existingSheet = context.workbook.worksheets.getItemOrNullObject(finalSheetName);
  await context.sync();

  if (existingSheet.isNullObject) {
    sheet = context.workbook.worksheets.add(finalSheetName);
  } else {
    sheet = existingSheet;
    sheet.getUsedRange().clear();
  }

  // Set column widths for professional layout
  const colLetters = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"];
  const colWidths = [66, 71, 71, 71, 66, 71, 66, 71, 71, 71, 66, 71];
  for (let i = 0; i < colLetters.length; i++) {
    sheet.getRange(`${colLetters[i]}:${colLetters[i]}`).format.columnWidth = colWidths[i];
  }
  await context.sync();

  let currentRow = 0;

  // 1. Dashboard header with executive summary
  try {
    currentRow = renderDashboardHeader(sheet, report, appliedFilters || "", currentRow, title || "Analysis Report");
    await context.sync();
    console.log("JESS: Dashboard header rendered at row", currentRow);
  } catch (err) {
    console.error("JESS: Failed to render dashboard header:", err);
    currentRow = 5;
  }

  // 2. Top metrics dashboard
  if (report.top_metrics && report.top_metrics.length > 0) {
    try {
      currentRow = renderTopMetrics(sheet, report.top_metrics, currentRow);
      await context.sync();
      console.log("JESS: Top metrics rendered at row", currentRow);
    } catch (err) {
      console.error("JESS: Failed to render top metrics:", err);
      currentRow += 5;
    }
  }

  // 3. Content sections — group by ASIN when applicable (Market Watch)
  const asinGroups = groupSectionsByAsin(report.sections);

  if (asinGroups) {
    // Market Watch: ASIN banner → seasonality summary → Primary/Secondary label → section content
    for (const group of asinGroups) {
      try {
        currentRow = renderAsinHeader(sheet, group.asin, currentRow);
        await context.sync();
        console.log(`JESS: ASIN header "${group.asin}" rendered at row`, currentRow);
      } catch (err) {
        console.error(`JESS: Failed to render ASIN header "${group.asin}":`, err);
        currentRow += 2;
      }

      // Old seasonality callout rows hidden — seasonality now baked into opportunity numbers (2026-04-10)
      // eslint-disable-next-line no-constant-condition
      if (false && report.seasonality_summary) {
        const seasonality = report.seasonality_summary.find(s => s.asin === group.asin);
        if (seasonality?.summary) {
          try {
            currentRow = renderSeasonalitySummary(sheet, seasonality.summary, currentRow);
            await context.sync();
            console.log(`JESS: Seasonality summary for "${group.asin}" rendered at row`, currentRow);
          } catch (err) {
            console.error(`JESS: Failed to render seasonality summary for "${group.asin}":`, err);
            currentRow += 1;
          }
        }
      }

      for (const { section, isPrimary } of group.sections) {
        try {
          const combined = {
            ...section,
            title: buildSectionTitle(section.title, isPrimary),
            style_preset: isPrimary ? "rank1" as const : "rank2" as const,
          };
          currentRow = renderSection(sheet, combined, currentRow);
          await context.sync();
          console.log(
            `JESS: ${isPrimary ? "Primary" : "Secondary"} section "${section.title}" rendered at row`,
            currentRow
          );
        } catch (err) {
          console.error(`JESS: Failed to render section "${section.title}":`, err);
          currentRow += 5;
        }
      }
    }
  } else {
    // Non-Market-Watch (RCA etc): existing flat loop
    for (let i = 0; i < report.sections.length; i++) {
      const section = report.sections[i];
      try {
        currentRow = renderSection(sheet, section, currentRow);
        await context.sync();
        console.log(`JESS: Section ${i + 1} "${section.title}" rendered at row`, currentRow);
      } catch (err) {
        console.error(`JESS: Failed to render section ${i + 1} "${section.title}":`, err);
        currentRow += 5;
      }
    }
  }

  // 4. Footer
  try {
    currentRow = renderFooter(sheet, report, currentRow);
    await context.sync();
    console.log("JESS: Footer rendered at row", currentRow);
  } catch (err) {
    console.error("JESS: Failed to render footer:", err);
  }

  return sheet;
}

// ============================================================
// HTML-TO-JESS BACKWARD COMPATIBILITY CONVERTER
// ============================================================

/**
 * Convert existing HTML report + summary into JESS format.
 * Used for backward compatibility when backend returns HTML instead of JESS.
 */
export function convertHtmlToJess(htmlReport: string, summary: string): ExcelStyledReport {
  const sections: Section[] = [];

  // Parse HTML sections using regex
  const sectionRegex =
    /<div\s+class="insights-section">([\s\S]*?)<\/div>\s*(?=<div\s+class="insights-section">|$)/g;
  let sectionMatch: RegExpExecArray | null;

  while ((sectionMatch = sectionRegex.exec(htmlReport)) !== null) {
    const sectionHtml = sectionMatch[1];

    // Extract title
    const titleMatch = sectionHtml.match(/<h[123][^>]*>(.*?)<\/h[123]>/);
    const title = titleMatch ? titleMatch[1].replace(/<[^>]+>/g, "").trim() : "Analysis";

    // Extract paragraphs
    const paragraphs: Paragraph[] = [];
    const pRegex = /<p[^>]*>([\s\S]*?)<\/p>/g;
    let pMatch: RegExpExecArray | null;
    while ((pMatch = pRegex.exec(sectionHtml)) !== null) {
      const text = pMatch[1]
        .replace(/<strong>/g, "**")
        .replace(/<\/strong>/g, "**")
        .replace(/<[^>]+>/g, "")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .trim();
      if (text) {
        paragraphs.push({ text, style: "normal" });
      }
    }

    // Extract list items
    const listItems: ListItem[] = [];
    const liRegex = /<li[^>]*>([\s\S]*?)<\/li>/g;
    let liMatch: RegExpExecArray | null;
    while ((liMatch = liRegex.exec(sectionHtml)) !== null) {
      const liHtml = liMatch[1];

      // Extract text
      const textMatch = liHtml.match(/class="item-text"[^>]*>([\s\S]*?)<\/div>/);
      const text = textMatch
        ? textMatch[1]
            .replace(/<strong>/g, "**")
            .replace(/<\/strong>/g, "**")
            .replace(/<[^>]+>/g, "")
            .trim()
        : liHtml.replace(/<[^>]+>/g, "").trim();

      // Extract badge
      const badgeMatch = liHtml.match(/class="metric-badge\s+(\w+)"[^>]*>([\s\S]*?)<\/span>/);
      const badge: Badge | undefined = badgeMatch
        ? { text: badgeMatch[2].trim(), type: badgeMatch[1] as BadgeType }
        : undefined;

      if (text) {
        listItems.push({ text, badge });
      }
    }

    // Extract metric cards
    const metrics: MetricCard[] = [];
    const metricRegex = /<div\s+class="metric-card">([\s\S]*?)<\/div>\s*<\/div>/g;
    let metricMatch: RegExpExecArray | null;
    while ((metricMatch = metricRegex.exec(sectionHtml)) !== null) {
      const metricHtml = metricMatch[1];
      const valueMatch = metricHtml.match(/class="metric-value"[^>]*>([\s\S]*?)<\/div>/);
      const labelMatch = metricHtml.match(/class="metric-label"[^>]*>([\s\S]*?)<\/div>/);
      const badgeMatch = metricHtml.match(/class="metric-badge\s+(\w+)"[^>]*>([\s\S]*?)<\/span>/);

      if (valueMatch && labelMatch) {
        metrics.push({
          label: labelMatch[1].replace(/<[^>]+>/g, "").trim(),
          value: valueMatch[1].replace(/<[^>]+>/g, "").trim(),
          badge: badgeMatch
            ? { text: badgeMatch[2].trim(), type: badgeMatch[1] as BadgeType }
            : undefined,
        });
      }
    }

    // Build section (only include non-empty content)
    const section: Section = { title };
    if (paragraphs.length > 0) section.paragraphs = paragraphs;
    if (listItems.length > 0) section.list_items = listItems;
    if (metrics.length > 0) section.metrics = metrics;

    // Use first paragraph as summary
    if (paragraphs.length > 0) {
      section.summary = paragraphs[0].text;
      section.paragraphs = paragraphs.slice(1);
      if (section.paragraphs.length === 0) delete section.paragraphs;
    }

    sections.push(section);
  }

  // Fallback: if no sections parsed, create one from summary
  if (sections.length === 0) {
    sections.push({
      title: "Analysis Summary",
      summary: summary || "Analysis completed.",
    });
  }

  return {
    executive_summary: summary || "Analysis completed.",
    sections,
    generated_at: new Date().toISOString(),
  };
}
